import verdeoHomeImage from "@/assets/images/verdeo-home.jpg";
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import DefaultFormJson from "@/DefaultForm.json";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import type { Step } from "@/pages/parameters/ParametersForms";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileStack,
  FolderKanban,
  Layers3,
  Loader,
  RefreshCw,
  Send,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CallSpecificFormBuilder, {
  buildInitializePayloadFromDraft,
  hydrateDraftSteps,
  type ProjectTypeOption,
} from "./CallSpecificFormBuilder";

const API_URL = import.meta.env.VITE_API_ENDPOINT;
const BRAND = "#1d5214";
const DEFAULT_FORM: Step[] = DefaultFormJson as Step[];

type FormStrategy = "existing_model" | "call_specific";
type WizardStep = 1 | 2 | 3 | 4;

type LibraryItem = {
  scope: "project_type" | "call";
  projectTypeId?: number;
  callId?: number;
  preservedCallId?: number | null;
  programId?: number | null;
  name: string;
  sourceDeleted?: boolean;
};

type SourceField = {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: unknown;
  nativeField?: string | null;
  step?: number;
};

type SourceSection = {
  name: string;
  label: string;
  description?: string | null;
  step?: number;
  fields?: SourceField[];
};

type SourceStep = {
  id?: number;
  name: string;
  label: string;
  description?: string | null;
  options?: unknown;
  step?: number;
  fields?: SourceField[];
  sections?: SourceSection[];
};

type CountryOption = {
  id: number;
  label: string;
};

type ModelSourcePayload =
  | { scope: "project_type"; projectTypeId: number }
  | { scope: "call" };

const stepTitles: Record<WizardStep, string> = {
  1: "Informations du programme",
  2: "Introduction",
  3: "Modele de formulaire",
  4: "Validation finale",
};

const getDefaultIntroImageSrc = (): string => verdeoHomeImage;

const isDefaultIntroImage = (value: string | null): boolean =>
  Boolean(
    value &&
    (value.includes("verdeo-home.jpg") || value.includes(verdeoHomeImage)),
  );

const escapeHtml = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const buildDescriptionParagraphs = (description: string): string => {
  const blocks = description
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);

  return blocks
    .map(
      (block) =>
        `<p style="margin:0 0 12px;font-size:16px;line-height:1.6;color:#374151;">${escapeHtml(block).replaceAll("\n", "<br />")}</p>`,
    )
    .join("");
};

const buildDefaultIntroductionContent = (
  programName: string,
  projectOwnerName: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) => ({
  title: `${t("Le porteur de projet {{ppName}} vous invite à répondre à l'appel à projets", { ppName: projectOwnerName })}${programName ? ` ${programName}` : ""}`,
  description: [
    t(
      "Un porteur de projet vous a transmis ce lien afin de vous permettre de soumettre votre candidature dans le cadre de son appel à projets sur Verdeo.",
    ),
    t(
      "Pour accéder à cet appel à projets et déposer votre dossier, nous vous invitons à créer votre compte sur la plateforme. Vous pourrez ainsi présenter votre projet directement au porteur concerné.",
    ),
  ].join("\n\n"),
  imageSrc: getDefaultIntroImageSrc(),
});

const buildIntroductionHtml = ({
  imageSrc,
  title,
  description,
}: {
  imageSrc: string;
  title: string;
  description: string;
}) => `
  <div style="display:flex;flex-wrap:wrap;align-items:center;gap:24px;">
    <div style="flex:1 1 280px;">
      <img src="${imageSrc}" alt="intro" style="display:block;width:100%;max-width:100%;border-radius:12px;object-fit:cover;height:auto;" />
    </div>
    <div style="flex:1 1 360px;">
      <h2 style="margin:0 0 16px;font-size:24px;font-weight:700;line-height:1.3;">${escapeHtml(title)}</h2>
      ${buildDescriptionParagraphs(description)}
    </div>
  </div>
`;

const asArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value["hydra:member"])) {
    return value["hydra:member"] as T[];
  }
  return [];
};

const CALL_CANDIDATE_HIDDEN_FIELD_KEYS = new Set([
  "name",
  "description",
  "type",
  "projecttype",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
]);

const normalizeFormKey = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

const isIntroLikeStep = (step: Pick<Step, "apiName" | "nameStep" | "type"> | SourceStep): boolean => {
  const apiName = normalizeFormKey("apiName" in step ? step.apiName : step.name);
  const label = normalizeFormKey("nameStep" in step ? step.nameStep : step.label);
  const type = normalizeFormKey(step.type);

  return apiName === "introduction" || label === "introduction" || type === "intro";
};

const isCandidateVisibleField = (field: { name?: string | null; nativeField?: string | null; type?: string | null }): boolean => {
  const key = normalizeFormKey(field.nativeField || field.name);
  const type = normalizeFormKey(field.type);

  return key !== "" && type !== "section" && !CALL_CANDIDATE_HIDDEN_FIELD_KEYS.has(key);
};

const hasCandidateFillableFieldInDraft = (steps: Step[]): boolean =>
  steps.some((step) => {
    if (isIntroLikeStep(step)) return false;

    return (
      (step.fields ?? []).some(isCandidateVisibleField) ||
      (step.sections ?? []).some((section) =>
        (section.fields ?? []).some(isCandidateVisibleField),
      )
    );
  });

const hasCandidateFillableFieldInSchema = (schema: SourceStep[]): boolean =>
  schema.some((step) => {
    if (isIntroLikeStep(step)) return false;

    return (
      (step.fields ?? []).some(isCandidateVisibleField) ||
      (step.sections ?? []).some((section) =>
        (section.fields ?? []).some(isCandidateVisibleField),
      )
    );
  });

const getLibraryItemKey = (item: LibraryItem) =>
  item.scope === "project_type"
    ? `project_type-${item.projectTypeId ?? 0}`
    : `call-${item.callId ?? item.preservedCallId ?? 0}`;

const isSameLibraryItem = (left: LibraryItem | null, right: LibraryItem) => {
  if (!left) return false;
  return getLibraryItemKey(left) === getLibraryItemKey(right);
};

export const buildInitializePayloadFromSchema = (schema: SourceStep[]) => ({
  steps: schema.map((step, index) => ({
    step: Number(step.step ?? index + 1),
    name: step.name,
    label: step.label,
    description: step.description ?? null,
    options:
      step.options && typeof step.options === "object" && !Array.isArray(step.options)
        ? { ...(step.options as Record<string, unknown>) }
        : null,
    type: step.name === "introduction" ? "intro" : "step",
    fields: Array.isArray(step.fields)
      ? step.fields.map((field, fieldIndex) => ({
          name: field.name,
          label: field.label,
          type: field.type,
          required: Boolean(field.required),
          options: field.options ?? null,
          nativeField: field.nativeField ?? null,
          step: Number(field.step ?? fieldIndex + 1),
        }))
      : [],
    sections: Array.isArray(step.sections)
      ? step.sections.map((section, sectionIndex) => ({
          nameSection: section.name,
          label: section.label,
          description: section.description ?? null,
          step: Number(section.step ?? sectionIndex + 1),
          fields: Array.isArray(section.fields)
            ? section.fields.map((field, fieldIndex) => ({
                name: field.name,
                label: field.label,
                type: field.type,
                required: Boolean(field.required),
                options: field.options ?? null,
                nativeField: field.nativeField ?? null,
                step: Number(field.step ?? fieldIndex + 1),
              }))
            : [],
        }))
      : [],
  })),
});

export const replaceCallSchema = async ({
  callId,
  postData,
  payload,
  modelSource,
}: {
  callId: number;
  postData: (params: { url: string; data: any }) => Promise<any>;
  payload: { steps: any[] };
  modelSource?: ModelSourcePayload;
}) => {
  const initResult = await postData({
    url: `${API_URL}/field_forms/replace-call-model`,
    data: {
      callId,
      steps: payload.steps,
      modelSource,
    },
  });

  if (!initResult) {
    throw new Error("Impossible de preparer le formulaire de l'appel.");
  }
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 last:border-b-0 last:pb-0">
    <span className="text-sm font-medium text-slate-500">{label}</span>
    <span className="text-right text-sm text-slate-800">{value}</span>
  </div>
);

const StepIndicator = ({ currentStep }: { currentStep: WizardStep }) => (
  <div className="flex flex-wrap items-center gap-3">
    {[1, 2, 3, 4].map((rawStep) => {
      const stepNumber = rawStep as WizardStep;
      const active = currentStep === stepNumber;
      const done = currentStep > stepNumber;
      return (
        <div key={stepNumber} className="flex items-center gap-3">
          <div
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
              done
                ? "border-[#1d5214] bg-[#1d5214] text-white"
                : active
                  ? "border-[#1d5214] bg-[#eef6ec] text-[#1d5214]"
                  : "border-slate-200 bg-white text-slate-500"
            }`}
          >
            {done ? <CheckCircle2 className="h-4 w-4" /> : stepNumber}
          </div>
          <div className="hidden sm:block">
            <p
              className={`text-xs font-semibold uppercase tracking-[0.18em] ${active || done ? "text-[#1d5214]" : "text-slate-400"}`}
            >
              Etape {stepNumber}
            </p>
            <p
              className={`text-sm ${active ? "font-semibold text-slate-900" : "text-slate-500"}`}
            >
              {stepTitles[stepNumber]}
            </p>
          </div>
          {stepNumber < 4 && (
            <div className="hidden h-px w-10 bg-slate-200 sm:block" />
          )}
        </div>
      );
    })}
  </div>
);

export default function GenerateNewLink({
  setData,
  onOptimisticCreate,
  onCreateSuccess,
  onCreateError,
}: {
  setData?: (data: any) => void;
  onOptimisticCreate?: (draftCall: any) => any;
  onCreateSuccess?: (createdCall: any, meta?: any) => void;
  onCreateError?: (meta?: any) => void;
}) {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "soustraitant",
  });
  const { user } = useAuth();
  const hidePostActions = useHidePostActions();
  const { fetchData } = useFetchDataV2("");
  const projectTypes = useFetchDataV2<any[]>(
    `${API_URL}/project-types`,
    "project-types-call-wizard",
    1000 * 60 * 30,
  );
  const countries = useFetchDataV2<any[]>(
    `${API_URL}/countries`,
    "countries-call-wizard",
    1000 * 60 * 30,
  );
  const { postData } = usePostData();
  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();

  const [open, setOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [isCheckingForms, setIsCheckingForms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [nomProgram, setNomProgram] = useState("");
  const [description, setDescription] = useState("");
  const [errorChercheur, setErrorChercheur] = useState(false);
  const [errorProjectType, setErrorProjectType] = useState(false);
  const [errorDateFin, setErrorDateFin] = useState(false);
  const [errorLaunchDate, setErrorLaunchDate] = useState(false);
  const [errorExpectedDuration, setErrorExpectedDuration] = useState(false);
  const [errorCountry, setErrorCountry] = useState(false);
  const [selectChercheur, setSelectChercheur] = useState("");
  const [selectedProjectTypeId, setSelectedProjectTypeId] =
    useState<string>("");
  const [dateFin, setDateFin] = useState("");
  const [hasLaunchDate, setHasLaunchDate] = useState(false);
  const [launchDate, setLaunchDate] = useState("");
  const [expectedDuration, setExpectedDuration] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const defaultIntro = buildDefaultIntroductionContent(
    nomProgram,
    `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim(),
    t,
  );
  const previousDefaultIntroTitleRef = useRef(defaultIntro.title);
  const previousDefaultIntroDescriptionRef = useRef(defaultIntro.description);
  const [introTitle, setIntroTitle] = useState(defaultIntro.title);
  const [introDescription, setIntroDescription] = useState(
    defaultIntro.description,
  );
  const [introImage, setIntroImage] = useState(defaultIntro.imageSrc);
  const [isUploadingIntroImage, setIsUploadingIntroImage] = useState(false);

  const [formStrategy, setFormStrategy] = useState<FormStrategy | null>(null);
  const [selectedModel, setSelectedModel] = useState<LibraryItem | null>(null);
  const [projectTypeForms, setProjectTypeForms] = useState<LibraryItem[]>([]);
  const [callForms, setCallForms] = useState<LibraryItem[]>([]);
  const [callSpecificDraftSteps, setCallSpecificDraftSteps] = useState<Step[]>(
    [],
  );
  const [callSpecificSelectedStep, setCallSpecificSelectedStep] = useState(1);
  const [hideWizardModalContent, setHideWizardModalContent] = useState(false);
  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const projectTypeOptions = useMemo<ProjectTypeOption[]>(
    () =>
      asArray<any>(projectTypes.data)
        .map((type: any) => ({
          id: Number(type?.id),
          label: String(type?.name ?? `Type ${type?.id ?? ""}`).trim(),
        }))
        .filter(
          (item) =>
            Number.isFinite(item.id) && item.id > 0 && item.label !== "",
        ),
    [projectTypes.data],
  );
  const countryOptions = useMemo<CountryOption[]>(
    () =>
      asArray<any>(countries.data)
        .map((country: any) => {
          const label =
            country?.countryLang?.find((item: any) => item?.lang?.code === "fr")
              ?.name ||
            country?.name ||
            `Pays ${country?.id ?? ""}`;

          return {
            id: Number(country?.id),
            label: String(label).trim(),
          };
        })
        .filter(
          (item) =>
            Number.isFinite(item.id) && item.id > 0 && item.label !== "",
        ),
    [countries.data],
  );

  const existingModelAvailable =
    projectTypeForms.length > 0 || callForms.length > 0;

  useEffect(() => {
    if (
      !introTitle.trim() ||
      introTitle === previousDefaultIntroTitleRef.current
    ) {
      setIntroTitle(defaultIntro.title);
    }
    if (
      !introDescription.trim() ||
      introDescription === previousDefaultIntroDescriptionRef.current
    ) {
      setIntroDescription(defaultIntro.description);
    }
    if (!introImage || isDefaultIntroImage(introImage)) {
      setIntroImage(defaultIntro.imageSrc);
    }

    previousDefaultIntroTitleRef.current = defaultIntro.title;
    previousDefaultIntroDescriptionRef.current = defaultIntro.description;
  }, [defaultIntro, introDescription, introImage, introTitle]);

  useEffect(() => {
    if (
      formStrategy === "call_specific" &&
      callSpecificDraftSteps.length === 0
    ) {
      setCallSpecificDraftSteps(hydrateDraftSteps(DEFAULT_FORM));
      setCallSpecificSelectedStep(1);
    }
  }, [formStrategy, callSpecificDraftSteps.length]);

  const isStepOneValid =
    nomProgram.trim() !== "" &&
    selectedProjectTypeId !== "" &&
    selectChercheur !== "" &&
    dateFin !== "" &&
    (!hasLaunchDate ||
      (launchDate !== "" &&
        expectedDuration !== "" &&
        Number(expectedDuration) > 0)) &&
    selectedCountryId !== "";
  const isStepThreeValid =
    formStrategy === "call_specific"
      ? callSpecificDraftSteps.length > 0
      : formStrategy === "existing_model" && selectedModel !== null;

  const resetWizard = () => {
    setWizardStep(1);
    setIsSubmitting(false);
    setNomProgram("");
    setDescription("");
    setErrorChercheur(false);
    setErrorProjectType(false);
    setErrorDateFin(false);
    setErrorLaunchDate(false);
    setErrorExpectedDuration(false);
    setErrorCountry(false);
    setSelectChercheur("");
    setSelectedProjectTypeId("");
    setDateFin("");
    setHasLaunchDate(false);
    setLaunchDate("");
    setExpectedDuration("");
    setSelectedCountryId("");
    const resetDefault = buildDefaultIntroductionContent("", "", t);
    setIntroTitle(resetDefault.title);
    setIntroDescription(resetDefault.description);
    setIntroImage(resetDefault.imageSrc);
    setFormStrategy(null);
    setSelectedModel(null);
    setCallSpecificDraftSteps([]);
    setCallSpecificSelectedStep(1);
    setHideWizardModalContent(false);
  };

  const onOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetWizard();
    }
    setOpen(nextOpen);
  };

  const loadFormsLibrary = async () => {
    setIsCheckingForms(true);
    try {
      const formsLibrary = await fetchData(`${API_URL}/field-forms/library`);
      setProjectTypeForms(
        Array.isArray(formsLibrary?.projectTypeForms)
          ? formsLibrary.projectTypeForms
          : [],
      );
      setCallForms(
        Array.isArray(formsLibrary?.callForms) ? formsLibrary.callForms : [],
      );
    } catch (_error) {
      setProjectTypeForms([]);
      setCallForms([]);
    } finally {
      setIsCheckingForms(false);
    }
  };

  const handleOpenCreateProgram = async () => {
    await loadFormsLibrary();
    setOpen(true);
  };

  const handleNext = () => {
    if (wizardStep === 1 && !isStepOneValid) {
      setErrorChercheur(selectChercheur === "");
      setErrorProjectType(selectedProjectTypeId === "");
      setErrorDateFin(dateFin === "");
      setErrorLaunchDate(hasLaunchDate && launchDate === "");
      setErrorExpectedDuration(
        hasLaunchDate &&
          (expectedDuration === "" || Number(expectedDuration) <= 0),
      );
      setErrorCountry(selectedCountryId === "");
      return;
    }

    if (wizardStep === 3 && !isStepThreeValid) {
      return;
    }

    setWizardStep((prev) => (prev + 1) as WizardStep);
  };

  const handleIntroImageChange = async (files: File[]) => {
    if (!files || files.length === 0) return;
    setIsUploadingIntroImage(true);
    try {
      const data = new FormData();
      const originalFile = files[0];
      let imageFile: File | Blob = originalFile;
      if (originalFile?.type?.startsWith("image/")) {
        const compressedBlob = await handleCompressImage(
          originalFile,
          1600,
          1600,
          0.9,
        );
        if (compressedBlob) {
          imageFile = new File([compressedBlob], originalFile.name, {
            type: compressedBlob.type || originalFile.type,
          });
        }
      }
      data.append("type", "8");
      if (imageFile) data.append("files[]", imageFile);

      const uploaded = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });

      if (
        !Array.isArray(uploaded) &&
        uploaded?.success !== undefined &&
        !uploaded.success
      ) {
        throw new Error(uploaded?.message || "Échec de l'upload.");
      }

      const media = Array.isArray(uploaded) ? uploaded[0] : null;
      if (media?.uri) {
        setIntroImage(
          String(media.uri).startsWith("http")
            ? media.uri
            : `${import.meta.env.VITE_MEDIA_BASE}${media.uri}`,
        );
      }
    } catch (_error) {
      toast({
        title: "Erreur",
        description: "L'image n'a pas pu être chargée.",
        duration: 3000,
      });
    } finally {
      setIsUploadingIntroImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!isStepOneValid || !isStepThreeValid) return;

    setIsSubmitting(true);
    let optimisticMeta: any = null;
    try {
      let selectedSourceSchema: SourceStep[] | null = null;
      if (formStrategy === "call_specific") {
        if (!hasCandidateFillableFieldInDraft(callSpecificDraftSteps)) {
          toast({
            title: "Formulaire incomplet",
            description:
              "Ajoutez au moins une étape contenant un champ à remplir par le candidat avant de créer l'appel à projets.",
            duration: 5000,
          });
          return;
        }
      }

      if (formStrategy === "existing_model" && selectedModel) {
        const query = new URLSearchParams();
        if (
          selectedModel.scope === "project_type" &&
          selectedModel.projectTypeId
        ) {
          query.set("projectTypeId", String(selectedModel.projectTypeId));
        }
        if (selectedModel.scope === "call" && selectedModel.callId) {
          query.set("callId", String(selectedModel.callId));
        }
        if (selectedModel.scope === "call" && selectedModel.preservedCallId) {
          query.set("preservedCallId", String(selectedModel.preservedCallId));
        }
        const sourceSchema = await fetchData(
          `${API_URL}/field-forms/global?${query.toString()}`,
        );
        if (!Array.isArray(sourceSchema) || sourceSchema.length === 0) {
          throw new Error("Modele source introuvable.");
        }
        selectedSourceSchema = sourceSchema as SourceStep[];
        if (!hasCandidateFillableFieldInSchema(selectedSourceSchema)) {
          toast({
            title: "Formulaire incomplet",
            description:
              "Le modèle sélectionné ne contient aucun champ visible à remplir par le candidat.",
            duration: 5000,
          });
          return;
        }
      }

      const introIsDefault =
        introTitle.trim() === defaultIntro.title.trim() &&
        introDescription.trim() === defaultIntro.description.trim() &&
        (introImage === defaultIntro.imageSrc ||
          isDefaultIntroImage(introImage));
      const introductionToSave = introIsDefault
        ? ""
        : buildIntroductionHtml({
            imageSrc: introImage,
            title: introTitle,
            description: introDescription,
          });
      const optimisticId = `optimistic-call-${Date.now()}`;
      const selectedProjectType = projectTypeOptions.find(
        (item) => item.id === Number(selectedProjectTypeId),
      );
      const selectedCountry = countryOptions.find(
        (item) => item.id === Number(selectedCountryId),
      );
      optimisticMeta = onOptimisticCreate?.({
        id: optimisticId,
        name: nomProgram,
        introduction: introductionToSave,
        dateFin,
        hasLaunchDate,
        launchDate: hasLaunchDate ? launchDate : null,
        expectedDuration: hasLaunchDate ? Number(expectedDuration) : null,
        country: selectedCountry
          ? {
              id: selectedCountry.id,
              name: selectedCountry.label,
            }
          : null,
        projectType: selectedProjectType
          ? {
              id: selectedProjectType.id,
              name: selectedProjectType.label,
            }
          : null,
        idProgramme: optimisticId,
        hasUsableForm: true,
        formMode: "call_specific",
        scoringEnabled: false,
        useCallSpecificForm: true,
        projectCount: 0,
        candidateCount: 0,
        statusCounts: {
          validated: 0,
          awaitingValidation: 0,
          rejected: 0,
          inProgress: 0,
          notStarted: 0,
        },
        canChangeForm: true,
        intervenants: [],
      });
      onOpenChange(false);

      const result = await postData({
        url: `${import.meta.env.VITE_IP_API}/api/call-for-project/add`,
        data: {
          nom: nomProgram,
          description,
          chercheur: selectChercheur,
          projectTypeId: Number(selectedProjectTypeId),
          dateFin,
          hasLaunchDate,
          launchDate: hasLaunchDate ? launchDate : null,
          expectedDuration: hasLaunchDate ? Number(expectedDuration) : null,
          countryId: Number(selectedCountryId),
          introduction: introductionToSave,
          formMode: "call_specific",
        },
      });

      const createdCallId = Number(result?.id ?? 0);
      if (!result || !Number.isFinite(createdCallId) || createdCallId <= 0) {
        throw new Error("La creation du programme a echoue.");
      }

      if (formStrategy === "existing_model" && selectedModel) {
        await replaceCallSchema({
          callId: createdCallId,
          postData,
          payload: buildInitializePayloadFromSchema(
            selectedSourceSchema as SourceStep[],
          ),
          modelSource: {
            scope: "project_type",
            projectTypeId: Number(selectedProjectTypeId),
          },
        });
      }

      if (formStrategy === "call_specific") {
        await replaceCallSchema({
          callId: createdCallId,
          postData,
          payload: buildInitializePayloadFromDraft(callSpecificDraftSteps),
          modelSource: {
            scope: "project_type",
            projectTypeId: Number(selectedProjectTypeId),
          },
        });
      }

      toast({
        title: "Programme créé",
        description:
          formStrategy === "call_specific"
            ? "Le programme et son formulaire dédié ont été créés."
            : "Le programme a été créé avec le modèle de formulaire sélectionné.",
        duration: 4500,
      });

      onCreateSuccess?.(
        {
          id: createdCallId,
          name: nomProgram,
          introduction: introductionToSave,
          dateFin,
          hasLaunchDate,
          launchDate: hasLaunchDate ? launchDate : null,
          expectedDuration: hasLaunchDate ? Number(expectedDuration) : null,
          country: selectedCountry
            ? {
                id: selectedCountry.id,
                name: selectedCountry.label,
              }
            : null,
          projectType: selectedProjectType
            ? {
                id: selectedProjectType.id,
                name: selectedProjectType.label,
              }
            : null,
          idProgramme: Number(result?.program?.id ?? result?.idProgramme ?? createdCallId),
          hasUsableForm: true,
          formMode: "call_specific",
          scoringEnabled: Boolean(result?.scoringEnabled),
          useCallSpecificForm: true,
          projectCount: 0,
          candidateCount: 0,
          statusCounts: {
            validated: 0,
            awaitingValidation: 0,
            rejected: 0,
            inProgress: 0,
            notStarted: 0,
          },
          canChangeForm: true,
          intervenants: [],
        },
        optimisticMeta,
      );
      if (typeof setData === "function") {
        return;
      }
    } catch (error: any) {
      onCreateError?.(optimisticMeta);
      toast({
        title: "Erreur",
        description:
          error?.message ||
          "Le programme n'a pas pu être créé correctement. Veuillez réessayer.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hidePostActions) return null;

  return (
    <>
      <Button
        className="bg-[#1d5214] text-white hover:bg-[#174010]"
        onClick={handleOpenCreateProgram}
        disabled={isCheckingForms}
      >
        {isCheckingForms ? (
          <Loader className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="mr-2 h-4 w-4" />
        )}
        {isCheckingForms
          ? "Chargement..."
          : t("Initier un programme pour appel à projet")}
      </Button>

      <Dialog modal={true} open={open} onOpenChange={onOpenChange}>
        <DialogContent
          id="generate-new-link-dialog-content"
          className={`z-[1200] sm:max-w-[1200px] ${hideWizardModalContent ? "opacity-0 pointer-events-none" : ""}`}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Initier un appel à projets</DialogTitle>
            <DialogDescription>
              Le programme et son formulaire ne seront créés qu'à l'étape de
              validation finale. Vous pouvez quitter ce parcours à tout moment
              sans générer d'éléments incomplets.
            </DialogDescription>
          </DialogHeader>

          <StepIndicator currentStep={wizardStep} />

          {wizardStep === 1 && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-[#d7e5d4] bg-[linear-gradient(135deg,#f7fbf6_0%,#eef6ec_100%)] p-5">
                <h3 className="text-lg font-semibold text-[#1d5214]">
                  Informations du programme
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#4c6f46]">
                  Renseignez les informations principales du programme. Cette
                  étape ne crée encore rien en base : elle prépare uniquement
                  les données à valider à la fin du parcours.
                </p>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Nom du programme
                  </label>
                  <Input
                    placeholder={t("Nom du programme")}
                    value={nomProgram}
                    onChange={(e) => setNomProgram(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Type de projet
                  </label>
                  <Select
                    value={selectedProjectTypeId}
                    onValueChange={(value) => {
                      setSelectedProjectTypeId(value);
                      setErrorProjectType(false);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un type de projet" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectTypeOptions.map((option) => (
                        <SelectItem key={option.id} value={String(option.id)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errorProjectType && (
                    <p className="text-sm text-red-500">
                      {t("Champ obligatoire")}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Date limite de dépôt des candidatures
                    </label>
                    <Input
                      type="date"
                      min={todayDate}
                      value={dateFin}
                      onChange={(e) => {
                        setDateFin(e.target.value);
                        setErrorDateFin(false);
                      }}
                      className="h-10"
                    />
                    {errorDateFin && (
                      <p className="text-sm text-red-500">
                        {t("Champ obligatoire")}
                      </p>
                    )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-slate-700">
                      Organisme chercheur de fonds
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <InfoCircledIcon className="cursor-pointer text-slate-500" />
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        {t("searchFinancingInternalDescription")}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Select
                    value={selectChercheur}
                    onValueChange={(value) => {
                      setSelectChercheur(value);
                      setErrorChercheur(false);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Organisme chercheur de fonds" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">
                        {t("Moi même et verdeo")}
                      </SelectItem>
                      <SelectItem value="1">{t("Moi même")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errorChercheur && (
                    <p className="text-sm text-red-500">
                      {t("Champ obligatoire")}
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <label
                      htmlFor="call-has-launch-date"
                      className="text-sm font-medium text-slate-700"
                    >
                      Définir une date de lancement
                    </label>
                    <p className="mt-1 text-xs text-slate-500">
                      Activez cette option pour renseigner la date de lancement
                      et la durée prévue du programme.
                    </p>
                  </div>
                  <Switch
                    id="call-has-launch-date"
                    checked={hasLaunchDate}
                    onCheckedChange={(checked) => {
                      setHasLaunchDate(checked);
                      setErrorLaunchDate(false);
                      setErrorExpectedDuration(false);
                      if (!checked) {
                        setLaunchDate("");
                        setExpectedDuration("");
                      }
                    }}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {hasLaunchDate && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Date de lancement
                      </label>
                      <Input
                        type="date"
                        value={launchDate}
                        onChange={(e) => {
                          setLaunchDate(e.target.value);
                          setErrorLaunchDate(false);
                        }}
                        className="h-10"
                      />
                      {errorLaunchDate && (
                        <p className="text-sm text-red-500">
                          {t("Champ obligatoire")}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Durée prévue
                      </label>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        placeholder="Durée en mois"
                        value={expectedDuration}
                        onChange={(e) => {
                          setExpectedDuration(e.target.value);
                          setErrorExpectedDuration(false);
                        }}
                        className="h-10"
                      />
                      {errorExpectedDuration && (
                        <p className="text-sm text-red-500">
                          Renseignez une durée supérieure à 0.
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Pays
                  </label>
                  <Select
                    value={selectedCountryId}
                    onValueChange={(value) => {
                      setSelectedCountryId(value);
                      setErrorCountry(false);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un pays" />
                    </SelectTrigger>
                    <SelectContent>
                      {countryOptions.map((option) => (
                        <SelectItem key={option.id} value={String(option.id)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errorCountry && (
                    <p className="text-sm text-red-500">
                      {t("Champ obligatoire")}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2 md:w-1/2">
                <label className="text-sm font-medium text-slate-700">
                  Description
                </label>
                <Textarea
                  placeholder={t("Description du programme")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[130px]"
                />
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-[#d7e5d4] bg-[linear-gradient(135deg,#f7fbf6_0%,#eef6ec_100%)] p-5">
                <h3 className="text-lg font-semibold text-[#1d5214]">
                  Introduction du programme
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#4c6f46]">
                  Personnalisez le contenu affiché aux candidats avant qu'ils
                  commencent leur candidature.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mt-4 flex flex-col gap-5 md:flex-row">
                  <div className="w-full md:w-2/5">
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Image
                    </label>
                    <FileUploader
                      value={[]}
                      onValueChange={handleIntroImageChange}
                      dropzoneOptions={{
                        multiple: false,
                        maxSize: 1024 * 1024 * 15,
                        accept: {
                          "image/*": [".jpg", ".jpeg", ".png", ".webp"],
                        },
                      }}
                      className="w-full"
                    >
                      <FileInput className="w-full rounded-lg border border-dashed border-slate-300 p-3">
                        <div className="flex min-h-[180px] items-center justify-center overflow-hidden rounded-md bg-white">
                          {isUploadingIntroImage ? (
                            <Loader className="h-6 w-6 animate-spin text-slate-500" />
                          ) : (
                            <img
                              src={introImage}
                              alt="Aperçu introduction"
                              className="max-h-[220px] w-full rounded-md object-cover"
                            />
                          )}
                        </div>
                      </FileInput>
                    </FileUploader>
                  </div>
                  <div className="w-full space-y-4 md:w-3/5">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Titre
                      </label>
                      <Input
                        value={introTitle}
                        onChange={(e) => setIntroTitle(e.target.value)}
                        placeholder="Titre de la landing page"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Description
                      </label>
                      <Textarea
                        value={introDescription}
                        onChange={(e) => setIntroDescription(e.target.value)}
                        className="min-h-[180px]"
                        placeholder="Description de l'appel à projets"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-[#d7e5d4] bg-[linear-gradient(135deg,#f7fbf6_0%,#eef6ec_100%)] p-5">
                <h3 className="text-lg font-semibold text-[#1d5214]">
                  Choix du formulaire
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#4c6f46]">
                  Choisissez si vous souhaitez réutiliser un modèle existant ou
                  créer un formulaire spécifique à cet appel. Toute la
                  configuration ci-dessous reste temporaire tant que vous n'avez
                  pas validé l'étape finale.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <button
                  type="button"
                  disabled={!existingModelAvailable}
                  onClick={() => {
                    setFormStrategy("existing_model");
                    setSelectedModel(null);
                  }}
                  className={`rounded-[24px] border p-6 text-left shadow-sm transition-all ${
                    existingModelAvailable
                      ? formStrategy === "existing_model"
                        ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                        : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-[#1d5214]"
                      : "cursor-not-allowed border-slate-200 bg-slate-100 opacity-60"
                  }`}
                >
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                    <FileStack className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1d5214]">
                    Utiliser un modèle existant
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Réutiliser un formulaire déjà configuré. Le modèle choisi
                    sera dupliqué sur le nouvel appel afin de conserver une
                    configuration autonome.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setFormStrategy("call_specific");
                    setSelectedModel(null);
                  }}
                  className={`rounded-[24px] border p-6 text-left shadow-sm transition-all ${
                    formStrategy === "call_specific"
                      ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                      : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-[#1d5214]"
                  }`}
                >
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                    <FolderKanban className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1d5214]">
                    Créer un formulaire pour cet appel
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Construire directement le formulaire dans ce parcours. Il
                    sera enregistré avec le programme uniquement lors de votre
                    validation finale.
                  </p>
                </button>
              </div>

              {formStrategy === "existing_model" && (
                <div className="space-y-6">
                  {!existingModelAvailable ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                      Aucun modèle existant n'est disponible pour le moment.
                    </div>
                  ) : (
                    <>
                      <section className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                            <Layers3 className="h-5 w-5" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-slate-900">
                              Modèles par type de projet
                            </h2>
                            <p className="text-sm text-slate-500">
                              {projectTypeForms.length} modèle
                              {projectTypeForms.length > 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                          {projectTypeForms.map((item) => (
                            <button
                              key={`type-${item.projectTypeId}`}
                              type="button"
                              onClick={() => setSelectedModel(item)}
                              className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border p-4 text-left shadow-sm transition-all ${
                                selectedModel?.scope === "project_type" &&
                                selectedModel?.projectTypeId ===
                                  item.projectTypeId
                                  ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                                  : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-emerald-300"
                              }`}
                            >
                              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
                                Type de projet
                              </p>
                              <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                                {item.name}
                              </h3>
                            </button>
                          ))}
                        </div>
                      </section>

                      <section className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
                            <FolderKanban className="h-5 w-5" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-slate-900">
                              Modèles d'appel à projets
                            </h2>
                            <p className="text-sm text-slate-500">
                              {callForms.length} modèle
                              {callForms.length > 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                          {callForms.map((item) => (
                            <button
                              key={getLibraryItemKey(item)}
                              type="button"
                              onClick={() => setSelectedModel(item)}
                              className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border p-4 text-left shadow-sm transition-all ${
                                isSameLibraryItem(selectedModel, item)
                                  ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                                  : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-lime-300"
                              }`}
                            >
                              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-lime-600">
                                {item.sourceDeleted
                                  ? "Appel supprimé"
                                  : "Appel à projets"}
                              </p>
                              <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                                {item.name}
                              </h3>
                              {item.sourceDeleted ? (
                                <p className="mt-2 text-xs leading-5 text-amber-700">
                                  Modèle conservé, déjà utilisé par un ou
                                  plusieurs projets.
                                </p>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      </section>
                    </>
                  )}
                </div>
              )}

              {formStrategy === "call_specific" && (
                <div className="space-y-5">
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                    Le formulaire ci-dessous est modifié localement dans cet
                    assistant. Si vous quittez ou annulez maintenant, aucun
                    programme ni formulaire ne sera créé.
                  </div>
                  <CallSpecificFormBuilder
                    steps={callSpecificDraftSteps}
                    selectedStep={callSpecificSelectedStep}
                    setSelectedStep={setCallSpecificSelectedStep}
                    setSteps={setCallSpecificDraftSteps}
                    projectTypeOptions={projectTypeOptions}
                    projectTypeOptionsLoading={projectTypes.loading}
                    onModalVisibilityChange={setHideWizardModalContent}
                    allowAdvancedScoringModes={true}
                  />
                </div>
              )}
            </div>
          )}

          {wizardStep === 4 && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-[#d7e5d4] bg-[linear-gradient(135deg,#f7fbf6_0%,#eef6ec_100%)] p-5">
                <h3 className="text-lg font-semibold text-[#1d5214]">
                  Validation finale
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#4c6f46]">
                  Contrôlez les informations ci-dessous avant de lancer la
                  création. Le programme et son formulaire seront créés ensemble
                  à cette étape.
                </p>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Résumé du programme
                  </h4>
                  <div className="mt-4 space-y-1">
                    <SummaryRow label="Nom" value={nomProgram || "-"} />
                    <SummaryRow
                      label="Type de projet"
                      value={
                        projectTypeOptions.find(
                          (item) => String(item.id) === selectedProjectTypeId,
                        )?.label || "-"
                      }
                    />
                    <SummaryRow
                      label="Date limite de dépôt"
                      value={dateFin || "-"}
                    />
                    <SummaryRow
                      label="Date de lancement"
                      value={hasLaunchDate ? launchDate || "-" : "Non définie"}
                    />
                    <SummaryRow
                      label="Durée prévue"
                      value={
                        hasLaunchDate && expectedDuration
                          ? `${expectedDuration} mois`
                          : "Non définie"
                      }
                    />
                    <SummaryRow
                      label="Pays"
                      value={
                        countryOptions.find(
                          (item) => String(item.id) === selectedCountryId,
                        )?.label || "-"
                      }
                    />
                    <SummaryRow
                      label="Description"
                      value={description || "Aucune description renseignée"}
                    />
                    <SummaryRow
                      label="Recherche de fonds"
                      value={
                        selectChercheur === "1"
                          ? "Moi-même"
                          : "Moi-même et Verdeo"
                      }
                    />
                    <SummaryRow
                      label="Introduction"
                      value={
                        introTitle.trim() === defaultIntro.title.trim() &&
                        introDescription.trim() ===
                          defaultIntro.description.trim() &&
                        (introImage === defaultIntro.imageSrc ||
                          isDefaultIntroImage(introImage))
                          ? "Modèle par défaut"
                          : "Introduction personnalisée"
                      }
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Stratégie de formulaire
                  </h4>
                  <div className="mt-4 space-y-1">
                    <SummaryRow
                      label="Mode"
                      value={
                        formStrategy === "call_specific"
                          ? "Formulaire dédié à cet appel"
                          : "Modèle existant"
                      }
                    />
                    <SummaryRow
                      label="Modele"
                      value={
                        formStrategy === "existing_model"
                          ? selectedModel?.name || "Aucun modèle sélectionné"
                          : `${callSpecificDraftSteps.length} étapes préparées`
                      }
                    />
                    <SummaryRow
                      label="Effet"
                      value={
                        formStrategy === "existing_model"
                          ? "Le modèle sera dupliqué sur l'appel afin de rester indépendant."
                          : "Le formulaire configuré dans cette fenêtre sera enregistré tel quel sur l'appel."
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Annuler
              </Button>
              {wizardStep > 1 && (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() =>
                    setWizardStep((prev) => (prev - 1) as WizardStep)
                  }
                  style={{ backgroundColor: BRAND }}
                  className="text-white hover:opacity-95"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Précédent
                </Button>
              )}
              {wizardStep < 4 && (
                <Button
                  type="button"
                  disabled={
                    (wizardStep === 1 && !isStepOneValid) ||
                    (wizardStep === 3 && !isStepThreeValid) ||
                    isSubmitting
                  }
                  onClick={handleNext}
                  style={{ backgroundColor: BRAND }}
                  className="text-white hover:opacity-95"
                >
                  <ChevronRight className="mr-2 h-4 w-4" />
                  Suivant
                </Button>
              )}
              {wizardStep === 4 && (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={
                    !isStepOneValid || !isStepThreeValid || isSubmitting
                  }
                  style={{ backgroundColor: BRAND }}
                  className="text-white hover:bg-[#174010]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                      Création en cours
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Initier le programme
                    </>
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
